import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { getSettings, adminUpdateSettings, adminGetNews } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const inputStyle = { background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" };
const Field = ({ label, children }) => (<div><label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>{label}</label>{children}</div>);
const Input = (props) => <input {...props} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />;
const Area = (props) => <textarea {...props} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />;

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: newsList = [] } = useQuery({ queryKey: ["admin-news"], queryFn: adminGetNews });
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setS({ address: {}, social: {}, seo: {}, policies: {}, phones: [], homepageFeaturedNews: [], homepageNewsMode: "latest", ...data }); }, [data]);

  if (isLoading || !s) return <AdminShell title="Settings & SEO"><div className="h-64 animate-pulse rounded-[14px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} /></AdminShell>;

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setNested = (parent, k, v) => setS((p) => ({ ...p, [parent]: { ...p[parent], [k]: v } }));

  const publishedNews = newsList.filter((n) => n.status === "published");
  const featured = s.homepageFeaturedNews || [];
  const toggleFeatured = (id) => {
    if (featured.includes(id)) { set("homepageFeaturedNews", featured.filter((x) => x !== id)); return; }
    if (featured.length >= 3) { toast.error("You can feature up to 3 articles"); return; }
    set("homepageFeaturedNews", [...featured, id]);
  };

  const save = async () => {
    setSaving(true);
    try { await adminUpdateSettings(s); await qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <AdminShell title="Settings & SEO" actions={
      <button onClick={save} disabled={saving} data-testid="admin-settings-save-button" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
        {saving && <Loader2 size={15} className="animate-spin" />} Save
      </button>
    }>
      <div className="max-w-2xl">
        <Tabs defaultValue="general" data-testid="admin-settings-tabs">
          <TabsList style={{ background: "var(--surface)" }}>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="homepage">Homepage</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
            <TabsTrigger value="policies">Policies</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="mt-6 space-y-4">
            <Field label="Company Name"><Input value={s.companyName || ""} onChange={(e) => set("companyName", e.target.value)} /></Field>
            <Field label="Tagline"><Input value={s.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></Field>
            <Field label="Motif"><Input value={s.motif || ""} onChange={(e) => set("motif", e.target.value)} /></Field>

            <div className="mt-2 flex items-center justify-between rounded-[10px] border px-3.5 py-3.5" style={inputStyle}>
              <div className="pr-4">
                <p className="text-sm" style={{ color: "var(--text)" }}>Leadership Page</p>
                <p className="mt-0.5 text-xs" style={{ color: "var(--muted)" }}>
                  When off, the Leadership page is hidden from navigation, the footer, the sitemap, and returns a 404. Content is preserved.
                </p>
              </div>
              <Switch
                data-testid="admin-leadership-toggle"
                checked={!!s.leadershipEnabled}
                onCheckedChange={(v) => set("leadershipEnabled", v)}
              />
            </div>
          </TabsContent>

          <TabsContent value="homepage" className="mt-6 space-y-4">
            <div className="flex items-center justify-between rounded-[10px] border px-3.5 py-3.5" style={inputStyle}>
              <div className="pr-4">
                <p className="text-sm" style={{ color: "var(--text)" }}>Show News on Homepage</p>
                <p className="mt-0.5 text-xs" style={{ color: "var(--muted)" }}>Display a News section on the homepage (max 3 articles). This does not affect the /news page, articles, or navigation.</p>
              </div>
              <Switch data-testid="admin-homepage-news-toggle" checked={!!s.showNewsOnHomepage} onCheckedChange={(v) => set("showNewsOnHomepage", v)} />
            </div>

            <Field label="Homepage News Mode">
              <Select value={s.homepageNewsMode || "latest"} onValueChange={(v) => set("homepageNewsMode", v)}>
                <SelectTrigger data-testid="admin-homepage-news-mode" style={inputStyle}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest — automatically show the 3 most recent published articles</SelectItem>
                  <SelectItem value="featured">Featured — manually select up to 3 published articles</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {s.homepageNewsMode === "featured" && (
              <div>
                <p className="mb-2 text-xs" style={{ color: "var(--muted)" }}>Select up to 3 published articles ({featured.length}/3 selected). Numbers indicate homepage order.</p>
                {publishedNews.length === 0 ? (
                  <p className="rounded-[10px] border px-3.5 py-3 text-sm" style={{ ...inputStyle, color: "var(--muted)" }}>No published articles yet. Publish articles to feature them here.</p>
                ) : (
                  <div className="space-y-2" data-testid="admin-homepage-featured-list">
                    {publishedNews.map((n) => {
                      const idx = featured.indexOf(n.id);
                      const selected = idx !== -1;
                      return (
                        <button key={n.id} type="button" onClick={() => toggleFeatured(n.id)} data-testid="admin-homepage-featured-item"
                          className="flex w-full items-center gap-3 rounded-[10px] border px-3 py-2.5 text-left"
                          style={{ borderColor: selected ? "var(--accent)" : "var(--card-border)", background: "var(--surface)" }}>
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px]" style={selected ? { background: "var(--accent)", color: "var(--accent-fg)" } : { border: "1px solid var(--card-border)", color: "var(--muted)" }}>{selected ? idx + 1 : ""}</span>
                          <span className="min-w-0 flex-1 truncate text-sm" style={{ color: "var(--text)" }}>{n.title}</span>
                          <span className="shrink-0 text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.14em" }}>{n.category}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="contact" className="mt-6 space-y-4">
            <Field label="Email"><Input value={s.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Phones (comma separated)"><Input value={(s.phones || []).join(", ")} onChange={(e) => set("phones", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></Field>
            <Field label="LinkedIn URL"><Input value={s.social?.linkedin || ""} onChange={(e) => setNested("social", "linkedin", e.target.value)} /></Field>
            <Field label="Google Maps Embed URL"><Input value={s.mapEmbedUrl || ""} onChange={(e) => set("mapEmbedUrl", e.target.value)} placeholder="https://www.google.com/maps?q=...&output=embed" /></Field>
            <Field label="Google Maps Link (Directions)"><Input value={s.mapLink || ""} onChange={(e) => set("mapLink", e.target.value)} /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {["line1", "line2", "line3", "line4"].map((l) => (
                <Field key={l} label={`Address ${l}`}><Input value={s.address?.[l] || ""} onChange={(e) => setNested("address", l, e.target.value)} /></Field>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="seo" className="mt-6 space-y-4">
            <Field label="Default Title"><Input value={s.seo?.defaultTitle || ""} onChange={(e) => setNested("seo", "defaultTitle", e.target.value)} /></Field>
            <Field label="Title Template (use %s for page)"><Input value={s.seo?.titleTemplate || ""} onChange={(e) => setNested("seo", "titleTemplate", e.target.value)} /></Field>
            <Field label="Default Description"><Area rows={3} value={s.seo?.defaultDescription || ""} onChange={(e) => setNested("seo", "defaultDescription", e.target.value)} /></Field>
            <Field label="Canonical Base URL"><Input value={s.seo?.canonicalBase || ""} onChange={(e) => setNested("seo", "canonicalBase", e.target.value)} /></Field>
            <Field label="OG Image URL"><Input value={s.seo?.ogImage || ""} onChange={(e) => setNested("seo", "ogImage", e.target.value)} /></Field>
          </TabsContent>

          <TabsContent value="policies" className="mt-6 space-y-4">
            <Field label="Privacy Policy"><Area rows={6} value={s.policies?.privacy || ""} onChange={(e) => setNested("policies", "privacy", e.target.value)} placeholder="Leave blank to use the default policy text." /></Field>
            <Field label="Terms of Use"><Area rows={6} value={s.policies?.terms || ""} onChange={(e) => setNested("policies", "terms", e.target.value)} placeholder="Leave blank to use the default terms text." /></Field>
          </TabsContent>
        </Tabs>
      </div>
    </AdminShell>
  );
}
